import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProdutoNaTabela } from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { EsqueletoDeTabela } from '../ui/estados.jsx';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Nota, Tabela, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * `#m-inventario` — a contagem física.
 *
 * A tela mostra o que o sistema acha que tem e recebe o que foi
 * contado; a diferença é calculada na hora, e a JUSTIFICATIVA fica
 * obrigatória assim que qualquer linha diverge. A mesma regra vale no
 * servidor e no CHECK da migration — aqui ela existe para a pessoa
 * saber antes de clicar, não como a única guarda.
 *
 * Quem não pode ver custo não vê o valor da perda: ele é calculado a
 * custo, e mostrá-lo seria o custo saindo pela porta dos fundos. A nota
 * ao vivo some pela falta do custo na tabela; a do aviso final, pelo
 * `perdaValor: null` que o servidor devolve — a guarda é a resposta.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function ModalDeInventario({ onFechar }: { onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const [contagem, setContagem] = useState<Record<string, string>>({});
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const produtos = useQuery({ queryKey: ['estoque', 'contagem'], queryFn: estoqueApi.contagem });

  const linhas = useMemo(
    () =>
      (produtos.data ?? []).map((produto) => {
        const digitado = contagem[produto.id];
        const contado = digitado === undefined || digitado === '' ? produto.saldo : Number(digitado);
        const valido = Number.isInteger(contado) && contado >= 0;
        return { produto, contado, valido, diferenca: valido ? contado - produto.saldo : 0 };
      }),
    [produtos.data, contagem],
  );

  const divergentes = linhas.filter((linha) => linha.diferenca !== 0);
  const temInvalido = linhas.some((linha) => !linha.valido);
  const podeVerCusto = produtos.data?.some((p) => typeof p.custo === 'number') ?? false;
  const perda = divergentes.reduce(
    (soma, linha) => soma - linha.diferenca * (linha.produto.custo ?? 0),
    0,
  );

  const fechar = useMutation({
    mutationFn: () =>
      estoqueApi.fecharInventario({
        itens: linhas.map((linha) => ({ produtoId: linha.produto.id, contado: linha.contado })),
        ...(justificativa.trim() ? { justificativa: justificativa.trim() } : {}),
        haDivergencia: divergentes.length > 0,
      }),
    onSuccess: async (resultado) => {
      await cliente.invalidateQueries({ queryKey: ['estoque'] });
      avisar(
        resultado.divergencias === 0
          ? 'Inventário fechado. A contagem bateu com o sistema.'
          : `Inventário fechado. ${
              resultado.perdaValor === null
                ? ''
                : `Perda de ${dinheiro(resultado.perdaValor)} registrada.`
            } ${resultado.divergencias} ${
              resultado.divergencias === 1 ? 'produto ajustado' : 'produtos ajustados'
            }.`.trim(),
      );
      onFechar();
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  const impedido =
    temInvalido || (divergentes.length > 0 && justificativa.trim().length < 3) || fechar.isPending;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Inventário"
      descricao="Contagem física com registro de quebra e perda."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => fechar.mutate()} disabled={impedido}>
            {fechar.isPending ? 'Fechando…' : 'Fechar inventário'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {erro && <Nota>{erro}</Nota>}

        {produtos.isPending ? (
          <EsqueletoDeTabela colunas={4} />
        ) : linhas.length === 0 ? (
          <Vazio>Nenhum produto cadastrado para contar.</Vazio>
        ) : (
          <Tabela colunas={['Produto', 'Sistema', 'Contado', 'Diferença']}>
            {linhas.map(({ produto, diferenca, valido }) => (
              <tr key={produto.id}>
                <td>{produto.nome}</td>
                <td className="num">{produto.saldo}</td>
                <td>
                  <input
                    type="number"
                    min={0}
                    style={{ width: 72 }}
                    aria-label={`Contagem de ${produto.nome}`}
                    value={contagem[produto.id] ?? String(produto.saldo)}
                    onChange={(e) =>
                      setContagem((atual) => ({ ...atual, [produto.id]: e.target.value }))
                    }
                  />
                </td>
                <td
                  className="num"
                  style={diferenca < 0 ? { color: 'var(--red-lt)' } : undefined}
                >
                  {!valido ? '—' : diferenca === 0 ? '0' : diferenca > 0 ? `+${diferenca}` : diferenca}
                </td>
              </tr>
            ))}
          </Tabela>
        )}

        <Campo
          rotulo="Justificativa do ajuste"
          ajuda={
            divergentes.length === 0
              ? 'Só é exigida quando a contagem diverge do sistema.'
              : `${divergentes.length} ${
                  divergentes.length === 1 ? 'produto divergiu' : 'produtos divergiram'
                } — diga o que explica a diferença.`
          }
        >
          <textarea
            rows={2}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            placeholder="Ex.: duas garrafas quebradas no transporte"
          />
        </Campo>

        {divergentes.length > 0 && podeVerCusto && (
          <Nota>
            {perda >= 0 ? 'Perda' : 'Sobra'} de <b>{dinheiro(Math.abs(perda))}</b>, a valor de
            custo. O ajuste vira movimento de estoque com a justificativa registrada.
          </Nota>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

/** Exportado para o teste de formato: a diferença é o contado menos o sistema. */
export function diferencaDaLinha(produto: ProdutoNaTabela, contado: number): number {
  return contado - produto.saldo;
}
